/**
 * BasicInfoDrawer — Edit device info form (description, serial, behavior/profile, assignment).
 *
 * Uses the dp-drawer always-render pattern for smooth slide-in animation.
 * Conditionally renders:
 *   - Inovonics: Behavior dropdown (from useMapData)
 *   - Palcare:   Line Profile dropdown (from dspProfiles)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';
import { areaOptions, residentOptions } from '../../components/advLearnIn.js';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  description: initDesc, serialNum: initSerial, deviceUseId: initUseId,
  isAreaDevice, areaId: initAreaId, residentId: initResidentId,
  areas, residents, useMapData,
  /* Palcare-specific props */
  isPalcare, dspProfileId: initDspProfileId, dspProfiles,
}) => {
  const [description, setDescription] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [deviceUseId, setDeviceUseId] = useState(0);
  const [dspProfileId, setDspProfileId] = useState(0);
  const [linkedId, setLinkedId] = useState(0);
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setDescription(initDesc || '');
      setSerialNum(initSerial || '');
      setDeviceUseId(initUseId || 0);
      setDspProfileId(initDspProfileId || 0);
      setLinkedId(isAreaDevice ? (initAreaId || 0) : (initResidentId || 0));
      setErrors({});
    }
    prevOpen.current = open;
  });

  const validate = useCallback(() => {
    const errs = {};
    if (!serialNum || !/^[0-9]+$/.test(serialNum)) {
      errs.serialNum = 'Serial number must be numeric.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [serialNum]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      description: description.trim(),
      serialNum,
      deviceUseId,
      dspProfileId,
      linkedId,
    });
  }, [validate, onSave, description, serialNum, deviceUseId, dspProfileId, linkedId]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`} style={{ zIndex: 1001 }}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Device Info</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Location within area — area devices only, matching the condition the
            Basic Info card uses to show it. A resident device is identified by
            the person it belongs to, so placement text has nothing to describe.
            The value still round-trips on save for non-area devices because it's
            seeded from props and can't be edited while hidden. */}
        {isAreaDevice ? (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">
              Location within area
            </label>
            <input
              type="text"
              className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional... Bedroom, Bathroom, etc."
              autoFocus
            />
            {errors.description && <p className="dp-drawer__error">{errors.description}</p>}
          </div>
        ) : null}

        {/* Serial Number */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Serial Number <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.serialNum ? ' is-invalid' : ''}`}
            value={serialNum}
            autoFocus={!isAreaDevice}
            onChange={(e) => setSerialNum(e.target.value)}
            placeholder="Numeric serial number"
          />
          {errors.serialNum && <p className="dp-drawer__error">{errors.serialNum}</p>}
        </div>

        {/* Behavior (Inovonics) */}
        {!isPalcare && useMapData.length > 0 && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Behavior</label>
            <select
              className="dp-drawer__select"
              value={deviceUseId}
              onChange={(e) => setDeviceUseId(e.target.value)}
            >
              {useMapData.map((item, i) => (
                <option key={i} value={item.inovonics_device_use_id}>
                  {item.option_text || 'Standard'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Line Profile (Palcare) */}
        {isPalcare && dspProfiles?.length > 0 && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Line Profile</label>
            <select
              className="dp-drawer__select"
              value={dspProfileId}
              onChange={(e) => setDspProfileId(e.target.value)}
            >
              {dspProfiles.map((item, i) => (
                <option key={i} value={item.dsp_profile_id}>
                  {item.profile_name || 'No Profile'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Assignment — V2 SearchableSelect, matching the Add Resident drawer's
            area picker. Replaced the V1 SearchableDropdown (bootstrap markup with
            its own floating label), which looked nothing like the rest of the V2
            forms around it.

            Option shapes come from advLearnIn.js so the sorting and the search
            behaviour are identical to the advanced learn-in drawer: areas by name,
            residents by first name, and searching matches the raw fields rather
            than the composed label. */}
        <div className="dp-drawer__field">
          <label htmlFor="edit-linked" className="dp-drawer__label">
            {isAreaDevice ? 'Assigned Area' : 'Assigned Resident'}{' '}
            <span className="dp-drawer__required">*</span>
          </label>
          <SearchableSelect
            id="edit-linked"
            options={isAreaDevice ? areaOptions(areas) : residentOptions(residents)}
            value={linkedId}
            /* State stays numeric with 0 meaning unassigned — that is what onSave
               forwards to the API. SearchableSelect hands back the option's string
               value or null, so both directions are converted here rather than
               changing the payload's shape. */
            onChange={(val) => setLinkedId(val ? Number(val) : 0)}
            placeholder={isAreaDevice ? 'Search or select area...' : 'Search or select resident...'}
            ariaLabel={isAreaDevice ? 'Assigned Area' : 'Assigned Resident'}
            required
            isClearable
          />
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoDrawer;
